import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import type { AxiosError } from 'axios';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import useAuthGuard from '../../hooks/useAuthGuard';
import {
  createGroupType,
  createSubject,
  createSubTopic,
  createTag,
  createTestSeries,
  createTopic,
  deleteGroupType,
  deleteSubject,
  deleteSubTopic,
  deleteTag,
  deleteTestSeries,
  deleteTopic,
  fetchGroupTypes,
  fetchSubjects,
  fetchSubTopics,
  fetchTags,
  fetchTestSeries,
  fetchTopics,
  updateGroupType,
  updateSubject,
  updateSubTopic,
  updateTag,
  updateTestSeries,
  updateTopic,
} from '../../services/taxonomyService';
import type {
  SubjectPayload,
  SubjectResource,
  SubjectUpdatePayload,
  SubTopicPayload,
  SubTopicResource,
  SubTopicUpdatePayload,
  TagPayload,
  TagResource,
  TagUpdatePayload,
  TestSeriesPayload,
  TestSeriesResource,
  TestSeriesUpdatePayload,
  TopicPayload,
  TopicResource,
  TopicUpdatePayload,
  GroupTypePayload,
  GroupTypeResource,
  GroupTypeUpdatePayload,
} from '../../types';
import { SimpleTextInput } from '../../components/FormComponents/SimpleTextInput';
import { SimpleSelect } from '../../components/FormComponents/SimpleSelect';
import { SimpleSwitch } from '../../components/FormComponents/SimpleSwitch';

const defaultSubjectForm: SubjectPayload = {
  name: '',
  isActive: true,
};

const defaultTopicForm: TopicPayload = {
  name: '',
  subjectId: '',
  isActive: true,
};

const defaultSubTopicForm: SubTopicPayload & { subjectId: string } = {
  name: '',
  topicId: '',
  subjectId: '',
  isActive: true,
};

const defaultTagForm: TagPayload = {
  name: '',
  isActive: true,
};

const defaultTestSeriesForm: TestSeriesPayload = {
  name: '',
  isActive: true,
};

const defaultGroupTypeForm: GroupTypePayload = {
  name: '',
  isActive: true,
};

const extractErrorMessage = (error: unknown, fallback: string): string => {
  const axiosError = error as AxiosError<{ message?: string }>;
  return axiosError?.response?.data?.message ?? fallback;
};

const TaxonomiesPage = () => {
  useAuthGuard(['admin', 'super_admin']);

  const [activeTab, setActiveTab] = useState(0);
  const [subjects, setSubjects] = useState<SubjectResource[]>([]);
  const [topics, setTopics] = useState<TopicResource[]>([]);
  const [subTopics, setSubTopics] = useState<SubTopicResource[]>([]);
  const [tags, setTags] = useState<TagResource[]>([]);
  const [testSeries, setTestSeries] = useState<TestSeriesResource[]>([]);

  const [groupTypes, setGroupTypes] = useState<GroupTypeResource[]>([]);

  const [subjectForm, setSubjectForm] = useState(defaultSubjectForm);
  const [topicForm, setTopicForm] = useState(defaultTopicForm);
  const [subTopicForm, setSubTopicForm] = useState(defaultSubTopicForm);
  const [tagForm, setTagForm] = useState(defaultTagForm);
  const [testSeriesForm, setTestSeriesForm] = useState(defaultTestSeriesForm);

  const [groupTypeForm, setGroupTypeForm] = useState(defaultGroupTypeForm);

  const [editingSubject, setEditingSubject] = useState<(SubjectUpdatePayload & { id: string; name: string }) | null>(null);
  const [editingTopic, setEditingTopic] = useState<
    (TopicUpdatePayload & { id: string; name: string; subjectId: string }) | null
  >(null);
  const [editingSubTopic, setEditingSubTopic] = useState<
    (SubTopicUpdatePayload & { id: string; name: string; topicId: string; subjectId: string }) | null
  >(null);
  const [editingTag, setEditingTag] = useState<(TagUpdatePayload & { id: string; name: string }) | null>(null);
  const [editingTestSeries, setEditingTestSeries] = useState<
    (TestSeriesUpdatePayload & { id: string; name: string }) | null
  >(null);

  const [editingGroupType, setEditingGroupType] = useState<
    (GroupTypeUpdatePayload & { id: string; name: string }) | null
  >(null);

  const [deletingSubject, setDeletingSubject] = useState<SubjectResource | null>(null);
  const [deletingTopic, setDeletingTopic] = useState<TopicResource | null>(null);
  const [deletingSubTopic, setDeletingSubTopic] = useState<SubTopicResource | null>(null);
  const [deletingTag, setDeletingTag] = useState<TagResource | null>(null);
  const [deletingTestSeries, setDeletingTestSeries] = useState<TestSeriesResource | null>(null);
  const [deletingGroupType, setDeletingGroupType] = useState<GroupTypeResource | null>(null);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadTaxonomies = useCallback(
    async (signal?: AbortSignal) => {
      try {
        if (!signal?.aborted) {
          setLoading(true);
          setError('');
        }

        const [subjectsData, topicsData, subTopicsData, tagsData, testSeriesData, groupTypesData] = await Promise.all([
          fetchSubjects(signal),
          fetchTopics({}, signal),
          fetchSubTopics({}, signal),
          fetchTags({}, signal),
          fetchTestSeries({}, signal),
          fetchGroupTypes({}, signal),
        ]);

        if (!signal?.aborted) {
          setSubjects(subjectsData);
          setTopics(topicsData);
          setSubTopics(subTopicsData);
          setTags(tagsData);
          setTestSeries(testSeriesData);
          setGroupTypes(groupTypesData);
        }
      } catch (loadError) {
        if (!signal?.aborted) {
          console.error('Failed to load taxonomies', loadError);
          setError(extractErrorMessage(loadError, 'Failed to load taxonomy data'));
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    loadTaxonomies(controller.signal);
    return () => controller.abort();
  }, [loadTaxonomies]);

  useEffect(() => {
    setSubTopicForm((prev) => {
      if (!prev.subjectId) {
        return prev.topicId ? { ...prev, topicId: '' } : prev;
      }

      const topicMatches = topics.some(
        (topic) => topic.id === prev.topicId && topic.subjectId === prev.subjectId
      );

      if (!topicMatches) {
        return { ...prev, topicId: '' };
      }

      return prev;
    });
  }, [topics, subTopicForm.subjectId, subTopicForm.topicId]);

  const availableTopicsForForm = useMemo(
    () => topics.filter((topic) => topic.subjectId === subTopicForm.subjectId),
    [topics, subTopicForm.subjectId]
  );

  const subjectColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Subject',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'topicsCount',
        headerName: 'Topics',
        width: 110,
        valueGetter: (params) => params.row.topics?.length ?? 0,
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 130,
        valueGetter: (params) => (params.row.isActive ? 'Active' : 'Inactive'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={() =>
                setEditingSubject({
                  id: params.row.id,
                  name: params.row.name,
                  isActive: params.row.isActive,
                })
              }
              disabled={actionLoading}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeletingSubject(params.row)}
              disabled={actionLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [actionLoading]
  );

  const topicColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Topic',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'subject',
        headerName: 'Subject',
        flex: 1,
        minWidth: 180,
        valueGetter: (params) => params.row.subject?.name ?? '',
      },
      {
        field: 'subTopics',
        headerName: 'Sub Topics',
        width: 130,
        valueGetter: (params) => params.row.subTopics?.length ?? 0,
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 130,
        valueGetter: (params) => (params.row.isActive ? 'Active' : 'Inactive'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={() =>
                setEditingTopic({
                  id: params.row.id,
                  name: params.row.name,
                  isActive: params.row.isActive,
                  subjectId: params.row.subjectId,
                })
              }
              disabled={actionLoading}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeletingTopic(params.row)}
              disabled={actionLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [actionLoading]
  );

  const subTopicColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Sub Topic',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'topic',
        headerName: 'Topic',
        flex: 1,
        minWidth: 180,
        valueGetter: (params) => params.row.topic?.name ?? '',
      },
      {
        field: 'subject',
        headerName: 'Subject',
        flex: 1,
        minWidth: 180,
        valueGetter: (params) => params.row.topic?.subject?.name ?? '',
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 130,
        valueGetter: (params) => (params.row.isActive ? 'Active' : 'Inactive'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={() =>
                setEditingSubTopic({
                  id: params.row.id,
                  name: params.row.name,
                  isActive: params.row.isActive,
                  topicId: params.row.topicId,
                  subjectId: params.row.topic?.subjectId ?? '',
                })
              }
              disabled={actionLoading}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeletingSubTopic(params.row)}
              disabled={actionLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [actionLoading]
  );

  const tagColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Tag',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 130,
        valueGetter: (params) => (params.row.isActive ? 'Active' : 'Inactive'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={() =>
                setEditingTag({
                  id: params.row.id,
                  name: params.row.name,
                  isActive: params.row.isActive,
                })
              }
              disabled={actionLoading}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeletingTag(params.row)}
              disabled={actionLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [actionLoading]
  );

  const testSeriesColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Test Series',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 130,
        valueGetter: (params) => (params.row.isActive ? 'Active' : 'Inactive'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={() =>
                setEditingTestSeries({
                  id: params.row.id,
                  name: params.row.name,
                  isActive: params.row.isActive,
                })
              }
              disabled={actionLoading}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeletingTestSeries(params.row)}
              disabled={actionLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [actionLoading]
  );

  const groupTypeColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Group Type',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 130,
        valueGetter: (params) => (params.row.isActive ? 'Active' : 'Inactive'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={() =>
                setEditingGroupType({
                  id: params.row.id,
                  name: params.row.name,
                  isActive: params.row.isActive,
                })
              }
              disabled={actionLoading}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeletingGroupType(params.row)}
              disabled={actionLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [actionLoading]
  );

  const handleCreateSubject = async () => {
    if (!subjectForm.name.trim()) {
      setError('Subject name is required');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await createSubject({ ...subjectForm, name: subjectForm.name.trim() });
      setSuccessMessage('Subject created successfully');
      setSubjectForm(defaultSubjectForm);
      await loadTaxonomies();
    } catch (createError) {
      console.error('Failed to create subject', createError);
      setError(extractErrorMessage(createError, 'Failed to create subject'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateTopic = async () => {
    if (!topicForm.subjectId) {
      setError('Please select a subject for the topic');
      return;
    }

    if (!topicForm.name.trim()) {
      setError('Topic name is required');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await createTopic({ ...topicForm, name: topicForm.name.trim() });
      setSuccessMessage('Topic created successfully');
      setTopicForm(defaultTopicForm);
      await loadTaxonomies();
    } catch (createError) {
      console.error('Failed to create topic', createError);
      setError(extractErrorMessage(createError, 'Failed to create topic'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateSubTopic = async () => {
    if (!subTopicForm.subjectId) {
      setError('Please select a subject for the sub topic');
      return;
    }

    if (!subTopicForm.topicId) {
      setError('Please select a topic for the sub topic');
      return;
    }

    if (!subTopicForm.name.trim()) {
      setError('Sub topic name is required');
      return;
    }

    const payload: SubTopicPayload = {
      name: subTopicForm.name.trim(),
      topicId: subTopicForm.topicId,
      isActive: subTopicForm.isActive,
    };

    try {
      setActionLoading(true);
      setError('');
      await createSubTopic(payload);
      setSuccessMessage('Sub topic created successfully');
      setSubTopicForm(defaultSubTopicForm);
      await loadTaxonomies();
    } catch (createError) {
      console.error('Failed to create sub topic', createError);
      setError(extractErrorMessage(createError, 'Failed to create sub topic'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!tagForm.name.trim()) {
      setError('Tag name is required');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await createTag({ ...tagForm, name: tagForm.name.trim() });
      setSuccessMessage('Tag created successfully');
      setTagForm(defaultTagForm);
      await loadTaxonomies();
    } catch (createError) {
      console.error('Failed to create tag', createError);
      setError(extractErrorMessage(createError, 'Failed to create tag'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateTestSeries = async () => {
    if (!testSeriesForm.name.trim()) {
      setError('Test series name is required');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await createTestSeries({ ...testSeriesForm, name: testSeriesForm.name.trim() });
      setSuccessMessage('Test series created successfully');
      setTestSeriesForm(defaultTestSeriesForm);
      await loadTaxonomies();
    } catch (createError) {
      console.error('Failed to create test series', createError);
      setError(extractErrorMessage(createError, 'Failed to create test series'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateSubject = async () => {
    if (!editingSubject) {
      return;
    }

    if (!editingSubject.name?.trim()) {
      setError('Subject name is required');
      return;
    }

    const payload: SubjectUpdatePayload = {
      name: editingSubject.name.trim(),
      isActive: editingSubject.isActive,
    };

    try {
      setActionLoading(true);
      setError('');
      await updateSubject(editingSubject.id, payload);
      setSuccessMessage('Subject updated successfully');
      setEditingSubject(null);
      await loadTaxonomies();
    } catch (updateError) {
      console.error('Failed to update subject', updateError);
      setError(extractErrorMessage(updateError, 'Failed to update subject'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateTopic = async () => {
    if (!editingTopic) {
      return;
    }

    if (!editingTopic.subjectId) {
      setError('Please select a subject for the topic');
      return;
    }

    if (!editingTopic.name?.trim()) {
      setError('Topic name is required');
      return;
    }

    const payload: TopicUpdatePayload = {
      name: editingTopic.name.trim(),
      subjectId: editingTopic.subjectId,
      isActive: editingTopic.isActive,
    };

    try {
      setActionLoading(true);
      setError('');
      await updateTopic(editingTopic.id, payload);
      setSuccessMessage('Topic updated successfully');
      setEditingTopic(null);
      await loadTaxonomies();
    } catch (updateError) {
      console.error('Failed to update topic', updateError);
      setError(extractErrorMessage(updateError, 'Failed to update topic'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateSubTopic = async () => {
    if (!editingSubTopic) {
      return;
    }

    if (!editingSubTopic.subjectId) {
      setError('Please select a subject for the sub topic');
      return;
    }

    if (!editingSubTopic.topicId) {
      setError('Please select a topic for the sub topic');
      return;
    }

    if (!editingSubTopic.name?.trim()) {
      setError('Sub topic name is required');
      return;
    }

    const payload: SubTopicUpdatePayload = {
      name: editingSubTopic.name.trim(),
      topicId: editingSubTopic.topicId,
      isActive: editingSubTopic.isActive,
    };

    try {
      setActionLoading(true);
      setError('');
      await updateSubTopic(editingSubTopic.id, payload);
      setSuccessMessage('Sub topic updated successfully');
      setEditingSubTopic(null);
      await loadTaxonomies();
    } catch (updateError) {
      console.error('Failed to update sub topic', updateError);
      setError(extractErrorMessage(updateError, 'Failed to update sub topic'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag) {
      return;
    }

    if (!editingTag.name?.trim()) {
      setError('Tag name is required');
      return;
    }

    const payload: TagUpdatePayload = {
      name: editingTag.name.trim(),
      isActive: editingTag.isActive,
    };

    try {
      setActionLoading(true);
      setError('');
      await updateTag(editingTag.id, payload);
      setSuccessMessage('Tag updated successfully');
      setEditingTag(null);
      await loadTaxonomies();
    } catch (updateError) {
      console.error('Failed to update tag', updateError);
      setError(extractErrorMessage(updateError, 'Failed to update tag'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateTestSeries = async () => {
    if (!editingTestSeries) {
      return;
    }

    if (!editingTestSeries.name?.trim()) {
      setError('Test series name is required');
      return;
    }

    const payload: TestSeriesUpdatePayload = {
      name: editingTestSeries.name.trim(),
      isActive: editingTestSeries.isActive,
    };

    try {
      setActionLoading(true);
      setError('');
      await updateTestSeries(editingTestSeries.id, payload);
      setSuccessMessage('Test series updated successfully');
      setEditingTestSeries(null);
      await loadTaxonomies();
    } catch (updateError) {
      console.error('Failed to update test series', updateError);
      setError(extractErrorMessage(updateError, 'Failed to update test series'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateGroupType = async () => {
    if (!groupTypeForm.name.trim()) {
      setError('Group type name is required');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await createGroupType({ ...groupTypeForm, name: groupTypeForm.name.trim() });
      setSuccessMessage('Group type created successfully');
      setGroupTypeForm(defaultGroupTypeForm);
      await loadTaxonomies();
    } catch (createError) {
      console.error('Failed to create group type', createError);
      setError(extractErrorMessage(createError, 'Failed to create group type'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateGroupType = async () => {
    if (!editingGroupType) {
      return;
    }

    if (!editingGroupType.name?.trim()) {
      setError('Group type name is required');
      return;
    }

    const payload: GroupTypeUpdatePayload = {
      name: editingGroupType.name.trim(),
      isActive: editingGroupType.isActive,
    };

    try {
      setActionLoading(true);
      setError('');
      await updateGroupType(editingGroupType.id, payload);
      setSuccessMessage('Group type updated successfully');
      setEditingGroupType(null);
      await loadTaxonomies();
    } catch (updateError) {
      console.error('Failed to update group type', updateError);
      setError(extractErrorMessage(updateError, 'Failed to update group type'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSubject = async () => {
    if (!deletingSubject) {
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await deleteSubject(deletingSubject.id);
      setSuccessMessage('Subject deleted successfully');
      setDeletingSubject(null);
      await loadTaxonomies();
    } catch (deleteError) {
      console.error('Failed to delete subject', deleteError);
      setError(extractErrorMessage(deleteError, 'Failed to delete subject'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTopic = async () => {
    if (!deletingTopic) {
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await deleteTopic(deletingTopic.id);
      setSuccessMessage('Topic deleted successfully');
      setDeletingTopic(null);
      await loadTaxonomies();
    } catch (deleteError) {
      console.error('Failed to delete topic', deleteError);
      setError(extractErrorMessage(deleteError, 'Failed to delete topic'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSubTopic = async () => {
    if (!deletingSubTopic) {
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await deleteSubTopic(deletingSubTopic.id);
      setSuccessMessage('Sub topic deleted successfully');
      setDeletingSubTopic(null);
      await loadTaxonomies();
    } catch (deleteError) {
      console.error('Failed to delete sub topic', deleteError);
      setError(extractErrorMessage(deleteError, 'Failed to delete sub topic'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTag = async () => {
    if (!deletingTag) {
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await deleteTag(deletingTag.id);
      setSuccessMessage('Tag deleted successfully');
      setDeletingTag(null);
      await loadTaxonomies();
    } catch (deleteError) {
      console.error('Failed to delete tag', deleteError);
      setError(extractErrorMessage(deleteError, 'Failed to delete tag'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTestSeries = async () => {
    if (!deletingTestSeries) {
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await deleteTestSeries(deletingTestSeries.id);
      setSuccessMessage('Test series deleted successfully');
      setDeletingTestSeries(null);
      await loadTaxonomies();
    } catch (deleteError) {
      console.error('Failed to delete test series', deleteError);
      setError(extractErrorMessage(deleteError, 'Failed to delete test series'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteGroupType = async () => {
    if (!deletingGroupType) {
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await deleteGroupType(deletingGroupType.id);
      setSuccessMessage('Group type deleted successfully');
      setDeletingGroupType(null);
      await loadTaxonomies();
    } catch (deleteError) {
      console.error('Failed to delete group type', deleteError);
      setError(extractErrorMessage(deleteError, 'Failed to delete group type'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const renderSubjectsTab = () => (
    <GridWrapper>
      <FormCard title="Add Subject">
        <Stack spacing={2}>
          <SimpleTextInput
            label="Subject Name"
            value={subjectForm.name}
            onChange={(value) => setSubjectForm((prev) => ({ ...prev, name: value }))}
            disabled={actionLoading}
          />
          <SimpleSwitch
            checked={subjectForm.isActive}
            onChange={(checked) => setSubjectForm((prev) => ({ ...prev, isActive: checked }))}
            label="Active"
            disabled={actionLoading}
          />
          <Button
            variant="contained"
            onClick={handleCreateSubject}
            disabled={actionLoading}
          >
            Add Subject
          </Button>
        </Stack>
      </FormCard>

      <DataGridCard>
        <DataGrid
          autoHeight
          rows={subjects}
          columns={subjectColumns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading || actionLoading}
        />
      </DataGridCard>
    </GridWrapper>
  );

  const renderTopicsTab = () => (
    <GridWrapper>
      <FormCard title="Add Topic">
        <Stack spacing={2}>
          <SimpleSelect
            label="Subject"
            value={topicForm.subjectId}
            onChange={(value) => setTopicForm((prev) => ({ ...prev, subjectId: value as string }))}
            disabled={actionLoading}
            displayEmpty
            emptyLabel="Select subject"
            options={subjects.map((subject) => ({
              value: subject.id,
              label: subject.name,
            }))}
          />
          <SimpleTextInput
            label="Topic Name"
            value={topicForm.name}
            onChange={(value) => setTopicForm((prev) => ({ ...prev, name: value }))}
            disabled={actionLoading}
          />
          <SimpleSwitch
            checked={topicForm.isActive}
            onChange={(checked) => setTopicForm((prev) => ({ ...prev, isActive: checked }))}
            label="Active"
            disabled={actionLoading}
          />
          <Button
            variant="contained"
            onClick={handleCreateTopic}
            disabled={actionLoading}
          >
            Add Topic
          </Button>
        </Stack>
      </FormCard>

      <DataGridCard>
        <DataGrid
          autoHeight
          rows={topics}
          columns={topicColumns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading || actionLoading}
        />
      </DataGridCard>
    </GridWrapper>
  );

  const renderSubTopicsTab = () => (
    <GridWrapper>
      <FormCard title="Add Sub Topic">
        <Stack spacing={2}>
          <SimpleSelect
            label="Subject"
            value={subTopicForm.subjectId}
            onChange={(value) =>
              setSubTopicForm((prev) => ({
                ...prev,
                subjectId: value as string,
              }))
            }
            disabled={actionLoading}
            displayEmpty
            emptyLabel="Select subject"
            options={subjects.map((subject) => ({
              value: subject.id,
              label: subject.name,
            }))}
          />
          <SimpleSelect
            label="Topic"
            value={subTopicForm.topicId}
            onChange={(value) => setSubTopicForm((prev) => ({ ...prev, topicId: value as string }))}
            disabled={!subTopicForm.subjectId || actionLoading}
            displayEmpty
            emptyLabel="Select topic"
            options={availableTopicsForForm.map((topic) => ({
              value: topic.id,
              label: topic.name,
            }))}
          />
          <SimpleTextInput
            label="Sub Topic Name"
            value={subTopicForm.name}
            onChange={(value) => setSubTopicForm((prev) => ({ ...prev, name: value }))}
            disabled={actionLoading}
          />
          <SimpleSwitch
            checked={subTopicForm.isActive}
            onChange={(checked) => setSubTopicForm((prev) => ({ ...prev, isActive: checked }))}
            label="Active"
            disabled={actionLoading}
          />
          <Button
            variant="contained"
            onClick={handleCreateSubTopic}
            disabled={actionLoading}
          >
            Add Sub Topic
          </Button>
        </Stack>
      </FormCard>

      <DataGridCard>
        <DataGrid
          autoHeight
          rows={subTopics}
          columns={subTopicColumns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading || actionLoading}
        />
      </DataGridCard>
    </GridWrapper>
  );

  const renderTagsTab = () => (
    <GridWrapper>
      <FormCard title="Add Tag">
        <Stack spacing={2}>
          <SimpleTextInput
            label="Tag Name"
            value={tagForm.name}
            onChange={(value) => setTagForm((prev) => ({ ...prev, name: value }))}
            disabled={actionLoading}
          />
          <SimpleSwitch
            checked={tagForm.isActive}
            onChange={(checked) => setTagForm((prev) => ({ ...prev, isActive: checked }))}
            label="Active"
            disabled={actionLoading}
          />
          <Button
            variant="contained"
            onClick={handleCreateTag}
            disabled={actionLoading}
          >
            Add Tag
          </Button>
        </Stack>
      </FormCard>

      <DataGridCard>
        <DataGrid
          autoHeight
          rows={tags}
          columns={tagColumns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading || actionLoading}
        />
      </DataGridCard>
    </GridWrapper>
  );

  const renderTestSeriesTab = () => (
    <GridWrapper>
      <FormCard title="Add Test Series">
        <Stack spacing={2}>
          <SimpleTextInput
            label="Test Series Name"
            value={testSeriesForm.name}
            onChange={(value) => setTestSeriesForm((prev) => ({ ...prev, name: value }))}
            disabled={actionLoading}
          />
          <SimpleSwitch
            checked={testSeriesForm.isActive}
            onChange={(checked) => setTestSeriesForm((prev) => ({ ...prev, isActive: checked }))}
            label="Active"
            disabled={actionLoading}
          />
          <Button
            variant="contained"
            onClick={handleCreateTestSeries}
            disabled={actionLoading}
          >
            Add Test Series
          </Button>
        </Stack>
      </FormCard>

      <DataGridCard>
        <DataGrid
          autoHeight
          rows={testSeries}
          columns={testSeriesColumns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading || actionLoading}
        />
      </DataGridCard>
    </GridWrapper>
  );

  const renderGroupTypesTab = () => (
    <GridWrapper>
      <FormCard title="Add Group Type">
        <Stack spacing={2}>
          <SimpleTextInput
            label="Group Type Name"
            value={groupTypeForm.name}
            onChange={(value) => setGroupTypeForm((prev) => ({ ...prev, name: value }))}
            disabled={actionLoading}
          />
          <SimpleSwitch
            checked={groupTypeForm.isActive}
            onChange={(checked) => setGroupTypeForm((prev) => ({ ...prev, isActive: checked }))}
            label="Active"
            disabled={actionLoading}
          />
          <Button
            variant="contained"
            onClick={handleCreateGroupType}
            disabled={actionLoading}
          >
            Add Group Type
          </Button>
        </Stack>
      </FormCard>

      <DataGridCard>
        <DataGrid
          autoHeight
          rows={groupTypes}
          columns={groupTypeColumns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading || actionLoading}
        />
      </DataGridCard>
    </GridWrapper>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderSubjectsTab();
      case 1:
        return renderTopicsTab();
      case 2:
        return renderSubTopicsTab();
      case 3:
        return renderTagsTab();
      case 4:
        return renderTestSeriesTab();
      case 5:
        return renderGroupTypesTab();
      default:
        return null;
    }
  };

  const availableTopicsForEdit = useMemo(
    () => (editingSubTopic ? topics.filter((topic) => topic.subjectId === editingSubTopic.subjectId) : []),
    [topics, editingSubTopic]
  );

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Taxonomies
        </Typography>
      </Stack>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Subjects" />
        <Tab label="Topics" />
        <Tab label="Sub Topics" />
        <Tab label="Tags" />
        <Tab label="Test Series" />
        <Tab label="Group Types" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        {loading ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <CardContent>{renderTabContent()}</CardContent>
        )}
      </Card>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setSuccessMessage('');
        }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={Boolean(editingSubject)}
        onClose={() => {
          if (!actionLoading) {
            setEditingSubject(null);
          }
        }}
      >
        <DialogTitle>Edit Subject</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SimpleTextInput
              label="Subject Name"
              value={editingSubject?.name ?? ''}
              onChange={(value) =>
                setEditingSubject((prev) =>
                  prev ? { ...prev, name: value } : prev
                )
              }
              disabled={actionLoading}
            />
            <SimpleSwitch
              checked={Boolean(editingSubject?.isActive)}
              onChange={(checked) =>
                setEditingSubject((prev) => (prev ? { ...prev, isActive: checked } : prev))
              }
              label="Active"
              disabled={actionLoading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingSubject(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleUpdateSubject} variant="contained" disabled={actionLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingTopic)}
        onClose={() => {
          if (!actionLoading) {
            setEditingTopic(null);
          }
        }}
      >
        <DialogTitle>Edit Topic</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SimpleSelect
              label="Subject"
              value={editingTopic?.subjectId ?? ''}
              onChange={(value) =>
                setEditingTopic((prev) =>
                  prev ? { ...prev, subjectId: value as string } : prev
                )
              }
              disabled={actionLoading}
              displayEmpty
              emptyLabel="Select subject"
              options={subjects.map((subject) => ({
                value: subject.id,
                label: subject.name,
              }))}
            />
            <SimpleTextInput
              label="Topic Name"
              value={editingTopic?.name ?? ''}
              onChange={(value) =>
                setEditingTopic((prev) =>
                  prev ? { ...prev, name: value } : prev
                )
              }
              disabled={actionLoading}
            />
            <SimpleSwitch
              checked={Boolean(editingTopic?.isActive)}
              onChange={(checked) =>
                setEditingTopic((prev) => (prev ? { ...prev, isActive: checked } : prev))
              }
              label="Active"
              disabled={actionLoading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingTopic(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleUpdateTopic} variant="contained" disabled={actionLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingSubTopic)}
        onClose={() => {
          if (!actionLoading) {
            setEditingSubTopic(null);
          }
        }}
      >
        <DialogTitle>Edit Sub Topic</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SimpleSelect
              label="Subject"
              value={editingSubTopic?.subjectId ?? ''}
              onChange={(value) =>
                setEditingSubTopic((prev) =>
                  prev ? { ...prev, subjectId: value as string, topicId: '' } : prev
                )
              }
              disabled={actionLoading}
              displayEmpty
              emptyLabel="Select subject"
              options={subjects.map((subject) => ({
                value: subject.id,
                label: subject.name,
              }))}
            />
            <SimpleSelect
              label="Topic"
              value={editingSubTopic?.topicId ?? ''}
              onChange={(value) =>
                setEditingSubTopic((prev) =>
                  prev ? { ...prev, topicId: value as string } : prev
                )
              }
              disabled={!editingSubTopic?.subjectId || actionLoading}
              displayEmpty
              emptyLabel="Select topic"
              options={availableTopicsForEdit.map((topic) => ({
                value: topic.id,
                label: topic.name,
              }))}
            />
            <SimpleTextInput
              label="Sub Topic Name"
              value={editingSubTopic?.name ?? ''}
              onChange={(value) =>
                setEditingSubTopic((prev) =>
                  prev ? { ...prev, name: value } : prev
                )
              }
              disabled={actionLoading}
            />
            <SimpleSwitch
              checked={Boolean(editingSubTopic?.isActive)}
              onChange={(checked) =>
                setEditingSubTopic((prev) => (prev ? { ...prev, isActive: checked } : prev))
              }
              label="Active"
              disabled={actionLoading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingSubTopic(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleUpdateSubTopic} variant="contained" disabled={actionLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingTag)}
        onClose={() => {
          if (!actionLoading) {
            setEditingTag(null);
          }
        }}
      >
        <DialogTitle>Edit Tag</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SimpleTextInput
              label="Tag Name"
              value={editingTag?.name ?? ''}
              onChange={(value) =>
                setEditingTag((prev) => (prev ? { ...prev, name: value } : prev))
              }
              disabled={actionLoading}
            />
            <SimpleSwitch
              checked={Boolean(editingTag?.isActive)}
              onChange={(checked) =>
                setEditingTag((prev) => (prev ? { ...prev, isActive: checked } : prev))
              }
              label="Active"
              disabled={actionLoading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingTag(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleUpdateTag} variant="contained" disabled={actionLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingTestSeries)}
        onClose={() => {
          if (!actionLoading) {
            setEditingTestSeries(null);
          }
        }}
      >
        <DialogTitle>Edit Test Series</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SimpleTextInput
              label="Test Series Name"
              value={editingTestSeries?.name ?? ''}
              onChange={(value) =>
                setEditingTestSeries((prev) =>
                  prev ? { ...prev, name: value } : prev
                )
              }
              disabled={actionLoading}
            />
            <SimpleSwitch
              checked={Boolean(editingTestSeries?.isActive)}
              onChange={(checked) =>
                setEditingTestSeries((prev) =>
                  prev ? { ...prev, isActive: checked } : prev
                )
              }
              label="Active"
              disabled={actionLoading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingTestSeries(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleUpdateTestSeries} variant="contained" disabled={actionLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingGroupType)}
        onClose={() => {
          if (!actionLoading) {
            setEditingGroupType(null);
          }
        }}
      >
        <DialogTitle>Edit Group Type</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SimpleTextInput
              label="Group Type Name"
              value={editingGroupType?.name ?? ''}
              onChange={(value) =>
                setEditingGroupType((prev) =>
                  prev ? { ...prev, name: value } : prev
                )
              }
              disabled={actionLoading}
            />
            <SimpleSwitch
              checked={Boolean(editingGroupType?.isActive)}
              onChange={(checked) =>
                setEditingGroupType((prev) =>
                  prev ? { ...prev, isActive: checked } : prev
                )
              }
              label="Active"
              disabled={actionLoading}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingGroupType(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleUpdateGroupType} variant="contained" disabled={actionLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletingSubject)}
        onClose={() => {
          if (!actionLoading) {
            setDeletingSubject(null);
          }
        }}
      >
        <DialogTitle>Delete Subject</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the subject "{deletingSubject?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingSubject(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteSubject} variant="contained" color="error" disabled={actionLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletingTopic)}
        onClose={() => {
          if (!actionLoading) {
            setDeletingTopic(null);
          }
        }}
      >
        <DialogTitle>Delete Topic</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the topic "{deletingTopic?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingTopic(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteTopic} variant="contained" color="error" disabled={actionLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletingSubTopic)}
        onClose={() => {
          if (!actionLoading) {
            setDeletingSubTopic(null);
          }
        }}
      >
        <DialogTitle>Delete Sub Topic</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the sub topic "{deletingSubTopic?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingSubTopic(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteSubTopic} variant="contained" color="error" disabled={actionLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletingTag)}
        onClose={() => {
          if (!actionLoading) {
            setDeletingTag(null);
          }
        }}
      >
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the tag "{deletingTag?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingTag(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteTag} variant="contained" color="error" disabled={actionLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletingTestSeries)}
        onClose={() => {
          if (!actionLoading) {
            setDeletingTestSeries(null);
          }
        }}
      >
        <DialogTitle>Delete Test Series</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the test series "{deletingTestSeries?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingTestSeries(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteTestSeries} variant="contained" color="error" disabled={actionLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletingGroupType)}
        onClose={() => {
          if (!actionLoading) {
            setDeletingGroupType(null);
          }
        }}
      >
        <DialogTitle>Delete Group Type</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the group type "{deletingGroupType?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingGroupType(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteGroupType} variant="contained" color="error" disabled={actionLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

interface FormCardProps {
  title: string;
  children: ReactNode;
}

const FormCard = ({ title, children }: FormCardProps) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

const DataGridCard = ({ children }: { children: ReactNode }) => (
  <Card variant="outlined">
    <CardContent>{children}</CardContent>
  </Card>
);

const GridWrapper = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
      gap: 3,
    }}
  >
    {children}
  </Box>
);

export default TaxonomiesPage;
